import { useEffect, useRef, useState } from 'preact/hooks';
import type { Credential } from '@/types';
import { ensureEzAuth, ensureEzAuthFacade, sharedOAuthConfig, socialProviderEnabled, SOCIAL_PROVIDERS, type EzAuthFacade, type SocialProvider } from '@/ensureEzAuth';
import { logError } from '@/sdkConsole';
import { createRequestScope } from '@/requestScope';
import { t, useT } from '@/i18n/t';

interface Props {
  // The domain whose ezAuth provider config drives the buttons. When absent
  // nothing renders (ezAuth identity is unavailable).
  domainId?: number;
  // Resolved once the visitor completes a provider sign-in: the same
  // ezAuth-token credential GoogleSignInButton produces, plus which provider
  // so the caller can record the identity method.
  onAuthenticated: (credential: Credential, provider: SocialProvider) => void;
  // Fired with whether any button is on screen, for the "or" divider.
  onAvailabilityChange?: (available: boolean) => void;
}

// Brand names are not translated.
const PROVIDER_NAMES: Record<SocialProvider, string> = {
  facebook: 'Facebook',
  apple: 'Apple',
  microsoft: 'Microsoft',
};

type Status = 'pending' | 'ready' | 'hidden';

// SocialSignInButtons renders a "Continue with <provider>" button for each
// Facebook/Apple/Microsoft provider the domain has enabled in ezAuth. The ezAuth
// SDK owns the popup and session exchange; this resolves the resulting session
// as a credential. Self-hides when no provider is enabled, the facade predates
// loginWithSocial, or setup fails, so the email/password path stays usable.
export function SocialSignInButtons({ domainId, onAuthenticated, onAvailabilityChange }: Props) {
  useT();
  const [status, setStatus] = useState<Status>('pending');
  const [providers, setProviders] = useState<SocialProvider[]>([]);
  const [error, setError] = useState<string | null>(null);
  const facadeRef = useRef<EzAuthFacade | null>(null);
  const mountedRef = useRef(true);
  // The SDK cancels an in-flight popup flow when a new one starts; only the
  // latest attempt may update state, so a superseded 'cancelled' can't clobber it.
  const [attemptRequests] = useState(() => createRequestScope());

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    void (async () => {
      if (typeof domainId !== 'number') {
        setStatus('hidden');
        return;
      }
      const facade = await ensureEzAuthFacade(domainId);
      if (!active) {
        return;
      }
      if (!facade || typeof facade.loginWithSocial !== 'function') {
        setStatus('hidden');
        return;
      }
      try {
        const config = await sharedOAuthConfig(facade);
        if (!active) {
          return;
        }
        const enabled = SOCIAL_PROVIDERS.filter((provider) => socialProviderEnabled(config, provider));
        if (enabled.length === 0) {
          setStatus('hidden');
          return;
        }
        facadeRef.current = facade;
        setProviders(enabled);
        setStatus('ready');
      } catch (err) {
        logError('social sign-in setup failed; Facebook/Apple/Microsoft sign-in is disabled here', err);
        if (active) {
          setStatus('hidden');
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [domainId]);

  useEffect(() => {
    if (status === 'ready') {
      onAvailabilityChange?.(true);
    } else if (status === 'hidden') {
      onAvailabilityChange?.(false);
    }
  }, [status, onAvailabilityChange]);

  const handleClick = (provider: SocialProvider) => {
    const facade = facadeRef.current;
    if (!facade?.loginWithSocial || typeof domainId !== 'number') {
      return;
    }
    attemptRequests.advance();
    const attempt = attemptRequests.capture();
    const isCurrent = () => mountedRef.current && attempt();
    setError(null);
    // Called synchronously in the click so the SDK's popup is not blocked.
    let login: Promise<unknown>;
    try {
      login = facade.loginWithSocial(provider);
    } catch (err) {
      login = Promise.reject(err);
    }
    void (async () => {
      try {
        await login;
        const token = await ensureEzAuth(domainId);
        if (!isCurrent()) {
          return;
        }
        if (token) {
          onAuthenticated({ ezAuthToken: token }, provider);
          return;
        }
        setError(t('socialSignIn.error', { provider: PROVIDER_NAMES[provider] }));
      } catch (err) {
        if (!isCurrent()) {
          return;
        }
        const message = socialErrorMessage(err, provider);
        if (message === null) {
          return;
        }
        logError(`${PROVIDER_NAMES[provider]} sign-in failed`, err);
        setError(message);
      }
    })();
  };

  if (status !== 'ready') {
    return null;
  }

  return (
    <div class="sm-social">
      {providers.map((provider) => (
        <button
          key={provider}
          type="button"
          class={`sm-btn sm-btn-secondary sm-social-btn sm-social-btn--${provider}`}
          onClick={() => handleClick(provider)}
        >
          <ProviderLogo provider={provider} />
          {t('socialSignIn.continueWith', { provider: PROVIDER_NAMES[provider] })}
        </button>
      ))}
      {error && <p class="sm-error" role="alert">{error}</p>}
    </div>
  );
}

// ProviderLogo draws the provider mark; same sources as staticsol
// node_js/ezauth/src/socialicons.ts:
//   facebook: Meta Facebook Brand Asset Pack, Facebook_Logo_Primary.ai (2023-09).
//   microsoft: learn.microsoft.com ms-symbollockup_mssymbol_19.svg.
//   apple: unverified pending Apple's artwork license; currentColor so it
//          shows in dark mode.
function ProviderLogo({ provider }: { provider: SocialProvider }) {
  const common = { class: 'sm-social-icon', width: 18, height: 18, 'aria-hidden': 'true', focusable: 'false' } as const;
  if (provider === 'facebook') {
    return (
      <svg {...common} viewBox="0 0 500 500">
        <path
          fill="#0866FF"
          d="M500 250C500 111.929 388.071 0 250 0C111.929 0 0 111.929 0 250C0 367.245 80.715 465.622 189.606 492.638L189.606 326.396L138.054 326.396L138.054 250L189.606 250L189.606 217.081C189.606 131.989 228.114 92.549 311.654 92.549C327.492 92.549 354.821 95.654 366.001 98.76L366.001 168.014C360.1 167.393 349.852 167.082 337.119 167.082C296.126 167.082 280.287 182.61 280.287 222.982L280.287 250L361.946 250L347.918 326.396L280.287 326.396L280.287 498.169C404.073 483.218 500 377.818 500 250Z"
        />
        {/* White "f" drawn explicitly so it stays white on dark buttons. */}
        <path
          fill="#FFFFFF"
          d="M347.918 326.396L361.947 250L280.288 250L280.288 222.981C280.288 182.609 296.125 167.082 337.119 167.082C349.852 167.082 360.1 167.392 366 168.013L366 98.76C354.821 95.654 327.492 92.548 311.654 92.548C228.115 92.548 189.606 131.989 189.606 217.081L189.606 250L138.054 250L138.054 326.396L189.606 326.396L189.606 492.638C208.949 497.436 229.173 500 250 500C260.254 500 270.358 499.368 280.288 498.169L280.288 326.396Z"
        />
      </svg>
    );
  }
  if (provider === 'microsoft') {
    return (
      <svg {...common} viewBox="0 0 21 21">
        <rect x="1" y="1" width="9" height="9" fill="#f25022" />
        <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
        <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
        <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
      </svg>
    );
  }
  return (
    <svg {...common} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.05 12.54c-.03-3.08 2.52-4.56 2.64-4.63-1.44-2.1-3.67-2.39-4.46-2.42-1.9-.19-3.71 1.12-4.67 1.12-.97 0-2.47-1.09-4.06-1.06-2.09.03-4.02 1.22-5.1 3.1-2.18 3.78-.56 9.38 1.57 12.45 1.04 1.5 2.28 3.18 3.91 3.12 1.57-.06 2.16-1.01 4.05-1.01 1.89 0 2.42 1.01 4.07.98 1.68-.03 2.75-1.53 3.78-3.03 1.19-1.74 1.68-3.43 1.71-3.52-.04-.02-3.28-1.26-3.44-5.1zM13.98 3.49C14.84 2.45 15.42 1 15.26-.44c-1.24.05-2.74.83-3.63 1.87-.8.93-1.5 2.42-1.31 3.85 1.38.11 2.8-.7 3.66-1.79z" />
    </svg>
  );
}

// socialErrorMessage maps an EzAuthError code to visitor copy; null means
// show nothing (the visitor closed the popup themselves).
function socialErrorMessage(err: unknown, provider: SocialProvider): string | null {
  const code = (err as { code?: string } | null)?.code;
  const name = PROVIDER_NAMES[provider];
  switch (code) {
    case 'cancelled':
      return null;
    case 'popup_blocked':
      return t('socialSignIn.popupBlocked', { provider: name });
    case 'email_required':
      return t('socialSignIn.emailRequired', { provider: name });
    case 'account_exists':
      return t('socialSignIn.accountExists');
    default:
      return t('socialSignIn.error', { provider: name });
  }
}
